import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Plus, Check, X, Loader2, Info, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { useProfile } from '@/hooks/useProfile';
import { AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';

interface Club {
  club_id: string;
  club_name: string;
  formatted_address?: string;
  city?: string;
  logo?: string;
  is_primary?: boolean;
  user_club_id?: string;
  id?: string;
}

export const ClubMembershipsCard: React.FC = () => {
  const { profile, fetchProfile } = useProfile();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [clubResults, setClubResults] = useState<Club[]>([]);
  const [userClubs, setUserClubs] = useState<Club[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');

  // State for pending invitations
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [pendingInvitationsCount, setPendingInvitationsCount] = useState<number>(0);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);

  // Fetch user's clubs
  const fetchUserClubs = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user's database ID
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData) throw new Error('User not found');

      // Get user's clubs using the RPC function
      const { data: clubs, error: clubsError } = await supabase.from('user_clubs')
        .select(`
          id,
          is_primary,
          clubs (
            club_id,
            club_name,
            formatted_address,
            city,
            logo
          )
        `)
        .eq('user_id', userData.id);

      if (clubsError) throw clubsError;

      // Transform the data to include user_club_id
      const transformedClubs = clubs?.map(membership => ({
        club_id: membership.clubs.club_id,
        club_name: membership.clubs.club_name,
        formatted_address: membership.clubs.formatted_address,
        city: membership.clubs.city,
        logo: membership.clubs.logo,
        is_primary: membership.is_primary,
        user_club_id: membership.id,
        id: membership.id // Keep a reference to the user_clubs.id
      })) || [];

      setUserClubs(transformedClubs);
    } catch (error) {
      console.error('Error fetching user clubs:', error);
      setError(error instanceof Error ? error.message : 'Failed to load clubs');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Search clubs
  const searchClubs = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setClubResults([]);
      return;
    }

    try {
      setIsSearching(true);
      setError(null);

      const { data, error } = await supabase
        .from('clubs')
        .select('club_id, club_name, formatted_address, city, logo')
        .ilike('club_name', `%${query}%`)
        .order('club_name')
        .limit(10);

      if (error) throw error;

      // Filter out clubs the user is already a member of
      const filteredResults = data?.filter(
        club => !userClubs.some(userClub => userClub.club_id === club.club_id)
      ) || [];

      setClubResults(filteredResults);
    } catch (error) {
      console.error('Error searching clubs:', error);
      setError(error instanceof Error ? error.message : 'Failed to search clubs');
    } finally {
      setIsSearching(false);
    }
  }, [userClubs]);

  // Add club membership
  const addClubMembership = async (clubId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user's database ID
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData) throw new Error('User not found');

      // Determine if this should be the primary club
      const isPrimary = userClubs.length === 0;

      // Get the club details first
      const { data: clubData, error: clubError } = await supabase
        .from('clubs')
        .select('club_id, club_name')
        .eq('club_id', clubId)
        .single();

      if (clubError) throw clubError;
      if (!clubData) throw new Error('Club not found');

      // Add club membership
      const { error: insertError } = await supabase
        .from('user_clubs')
        .insert({
          user_id: userData.id,
          club_id: clubId,
          is_primary: isPrimary
        });

      if (insertError) throw insertError;

      // If this is the first club, update the user's club_id field
      if (isPrimary) {
        const { error: updateError } = await supabase
          .from('users')
          .update({
            club_id: clubData.club_id,
            custom_club: null // Clear custom club when adding a real club
          })
          .eq('id', userData.id);

        if (updateError) throw updateError;
      }

      // Refresh clubs list
      await fetchUserClubs();
      await fetchProfile();
      
      // Clear search
      setSearchTerm('');
      setClubResults([]);
      setShowSearch(false);
    } catch (error) {
      console.error('Error adding club membership:', error);
      setError(error instanceof Error ? error.message : 'Failed to add club');
    } finally {
      setIsLoading(false);
    }
  };

  // Remove club membership
  const removeClubMembership = async (userClubId: string, isPrimary: boolean) => {
  console.log('Attempting to remove membership for club', userClubId, isPrimary);  // Add this log

  try {
    setIsLoading(true);
    setError(null);

    const { data: { user } } = await supabase.auth.getUser();
    console.log('Current user:', user); // Log user data

    if (!user) throw new Error('Not authenticated');

    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();

    if (userError) throw userError;
    if (!userData) throw new Error('User not found');

    // New: delete directly
const { data, error } = await supabase
  .from('user_clubs')
  .delete()
  .eq('id', userClubId)
  .eq('user_id', userData.id);

if (error) throw error;

    console.log('Membership removed successfully'); // Log on success

    await fetchUserClubs();
    await fetchProfile();
  } catch (error) {
    console.error('Error removing club membership:', error);  // Log error
    setError(error instanceof Error ? error.message : 'Failed to remove club');
  } finally {
    setIsLoading(false);
  }
};


  // Set primary club
  const setPrimaryClub = async (userClubId: string, clubId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user's database ID
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData) throw new Error('User not found');

      // Update the user_clubs record to set is_primary to true
      const { error: updateClubError } = await supabase
        .from('user_clubs')
        .update({
          is_primary: true
        })
        .eq('id', userClubId);

      if (updateClubError) throw updateClubError;

      // Get the club details
      const { data: clubData, error: clubError } = await supabase
        .from('clubs')
        .select('club_id')
        .eq('club_id', clubId)
        .single();

      if (clubError) throw clubError;
      if (!clubData) throw new Error('Club not found');

      // Update the user's club_id field
      const { error: updateUserError } = await supabase
        .from('users')
        .update({
          club_id: clubData.club_id
        })
        .eq('id', userData.id);

      if (updateUserError) throw updateUserError;

      // Refresh clubs list
      await fetchUserClubs();
      await fetchProfile();
    } catch (error) {
      console.error('Error setting primary club:', error);
      setError(error instanceof Error ? error.message : 'Failed to set primary club');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle accepting a club invitation (with notification for the inviter)
  const handleAcceptInvitation = async (invitationId: string) => {
    try {
      setIsLoading(true);

      // 1) Mark the invitation as “accepted”
      const { error: acceptError } = await supabase
        .from('club_invitations')
        .update({
          status: 'accepted',
          updated_at: new Date().toISOString(),
        })
        .eq('id', invitationId);
      if (acceptError) throw acceptError;

      // 2) Fetch the full invitation row (to get inviter_id, invitee_id, club_id)
      const { data: invitation, error: invitationError } = await supabase
        .from('club_invitations')
        .select('club_id, invitee_id, inviter_id, club:club_id(club_name)')
        .eq('id', invitationId)
        .single();
      if (invitationError || !invitation) throw invitationError;

      // 3) Add the invitee to user_clubs
      const { error: membershipError } = await supabase
        .from('user_clubs')
        .insert({
          user_id: invitation.invitee_id,
          club_id: invitation.club_id,
          is_primary: false,
        });
      if (membershipError) throw membershipError;

      // 4) Remove that invitation from local state so it disappears on the UI
      setPendingInvitations(prev =>
        prev.filter(inv => inv.id !== invitationId)
      );

      // 5) Look up the acceptor’s (invitee’s) full_name
      const { data: acceptorUser, error: acceptorError } = await supabase
        .from('users')
        .select('full_name')
        .eq('id', invitation.invitee_id)
        .single();
      const acceptorName = acceptorError || !acceptorUser
        ? 'Someone'
        : acceptorUser.full_name;

      // 6) Insert a “club_invitation_accepted” notification for the inviter
      const { error: notifyError } = await supabase
        .from('notifications')
        .insert([
          {
            user_id: invitation.inviter_id,   // who should see “X accepted…”
            sender_id: invitation.invitee_id, // who accepted
            type: 'club_invitation_accepted',
content: `${acceptorName} accepted your invitation to join ${invitation.club.club_name}.`,
            metadata: { club_id: invitation.club_id },
          },
        ]);
      if (notifyError) {
        console.error('Failed to insert accept notification:', notifyError);
        // Not blocking the rest of the flow on notification failure
      }

      // 7) Refresh the user’s club list
      await fetchUserClubs();

      // 8) Show a “accepted” toast to the accepting user
      toast({
        title: "Invitation accepted",
        description: "You have successfully joined the club",
        variant: "default",
      });
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast({
        title: "Error",
        description: "Failed to accept invitation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };


   // Handle declining a club invitation
  const handleDeclineInvitation = async (invitationId: string) => {
    try {
      setIsLoading(true);

      // 1) Mark the invitation as “declined”
      const { error: declineError } = await supabase
        .from('club_invitations')
        .update({
          status: 'declined',
          updated_at: new Date().toISOString()
        })
        .eq('id', invitationId);
      if (declineError) throw declineError;

      // 2) Fetch the invitation row so we know inviter_id, invitee_id, and club_id
      const { data: invitation, error: invitationError } = await supabase
        .from('club_invitations')
       .select('club_id, invitee_id, inviter_id, club:club_id(club_name)')
        .eq('id', invitationId)
        .single();
      if (invitationError || !invitation) throw invitationError;

      // 3) Remove the invitation from local state
      setPendingInvitations(prev => prev.filter(inv => inv.id !== invitationId));

      // 4a) Fetch the decliner’s (invitee’s) display name so we can build the notification message
      const { data: declineUser, error: declineUserError } = await supabase
        .from('users')
        .select('full_name')
        .eq('id', invitation.invitee_id)
        .single();
      const declinerName =
        declineUserError || !declineUser
          ? 'Someone'
          : declineUser.full_name;

     // 4b) Insert a “club_invitation_rejected” notification for the inviter
const { data: notifyData, error: notifyError } = await supabase
  .from('notifications')
  .insert({
    user_id:   invitation.inviter_id,       // who should see “X declined…”
    sender_id: invitation.invitee_id,       // who declined
    type:      'club_invitation_rejected',
content:   `${declinerName} declined your invitation to join ${invitation.club.club_name}.`,
    metadata:  { club_id: invitation.club_id }
  });

if (notifyError) {
  console.error('Failed to insert decline notification:', notifyError);
  throw notifyError;
}

console.log('Successfully inserted decline notification:', notifyData);

      // 5) Show a “declined” toast to the decliner
      toast({
        title: "Invitation declined",
        description: "You have declined the club invitation",
        variant: "default",
      });
    } catch (error) {
      console.error('Error declining invitation:', error);
      toast({
        title: "Error",
        description: "Failed to decline invitation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };


  // Navigate to club profile
  const navigateToClub = (clubId: string, event: React.MouseEvent) => {
    // Prevent navigation if clicking on buttons
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }
    navigate(`/dashboard/clubs/${clubId}`);
  };

  // Load clubs on mount
  useEffect(() => {
    fetchUserClubs();
  }, [fetchUserClubs]);

  // Fetch pending club invitations
  useEffect(() => {
    const fetchPendingInvitations = async () => {
      try {
        setIsLoadingInvitations(true);
        
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        if (!user) throw new Error('Not authenticated');

        // Get user's database ID
        const { data: userData, error: userDataError } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', user.id)
          .single();

        if (userDataError) throw userDataError;
        if (!userData) throw new Error('User not found');

        // Get pending invitations
        const { data: invitations, error: invitationsError } = await supabase
          .from('club_invitations')
          .select(`
            id,
            created_at,
            club:club_id(club_id, club_name, logo, formatted_address, city),
            inviter:inviter_id(id, auth_id, full_name, avatar_url)
          `)
          .eq('invitee_id', userData.id)
          .eq('status', 'pending')
          .order('created_at', { ascending: false });

        if (invitationsError) throw invitationsError;
        
        setPendingInvitations(invitations || []);
      } catch (error) {
        console.error('Error fetching pending invitations:', error);
        setPendingInvitationsCount(0);
      } finally {
        setIsLoadingInvitations(false);
      }
    };

    fetchPendingInvitations();
  }, []);

  // Update the pending invitations count whenever the invitations change
  useEffect(() => {
    setPendingInvitationsCount(pendingInvitations.length);
  }, [pendingInvitations]);

  // Search clubs when search term changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      searchClubs(searchTerm);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, searchClubs]);

  const renderDefaultClubLogo = (isPrimary: boolean) => (
    <div className={cn(
      "w-16 h-16 rounded-full flex items-center justify-center",
      "border-2",
      isPrimary ? "border-gold-400 bg-gold-400/10" : "border-dark-400 bg-dark-400"
    )}>
      <Shield className="h-8 w-8 text-gold-400" />
    </div>
  );

  return (
    <div className="bg-dark-200 rounded-xl p-6 border border-dark-300">
      <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
        <div className="flex-1 flex items-center">
          <Shield className="h-5 w-5 text-gold-400 mr-2" />
          Club Memberships
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('grid')}
            className={cn(
              "p-1.5 rounded-md transition-colors",
              viewMode === 'grid' 
                ? "bg-dark-300 text-gold-400" 
                : "text-dark-800 hover:text-white hover:bg-dark-300"
            )}
            title="Grid view"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="7" height="7" x="3" y="3" rx="1" /><rect width="7" height="7" x="14" y="3" rx="1" /><rect width="7" height="7" x="14" y="14" rx="1" /><rect width="7" height="7" x="3" y="14" rx="1" /></svg>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={cn(
              "p-1.5 rounded-md transition-colors",
              viewMode === 'list' 
                ? "bg-dark-300 text-gold-400" 
                : "text-dark-800 hover:text-white hover:bg-dark-300"
            )}
            title="List view"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" x2="21" y1="6" y2="6" /><line x1="8" x2="21" y1="12" y2="12" /><line x1="8" x2="21" y1="18" y2="18" /><line x1="3" x2="3.01" y1="6" y2="6" /><line x1="3" x2="3.01" y1="12" y2="12" /><line x1="3" x2="3.01" y1="18" y2="18" /></svg>
          </button>
        </div>
        {pendingInvitations.length > 0 && (
          <span className="ml-2 px-2 py-0.5 bg-gold-400 text-dark-50 text-xs rounded-full">
            {pendingInvitations.length} pending
          </span>
        )}
      </h2>

      {error && (
        <div className="bg-red-500/10 text-red-500 p-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Pending Invitations Section */}
      {pendingInvitations.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-medium text-white mb-4 flex items-center">
            <Info className="h-4 w-4 text-gold-400 mr-2" />
            Pending Invitations
          </h3>
          
          <div className="space-y-3">
            {isLoadingInvitations ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 text-gold-400 animate-spin" />
              </div>
            ) : (
              pendingInvitations.map((invitation) => (
                <div 
                  key={invitation.id}
                  className="bg-dark-300 rounded-lg p-4 border border-dark-400 hover:border-gold-400/50 transition-all duration-200"
                >
                  <div className="flex flex-col sm:flex-row items-start gap-4">
                    <div className="flex-shrink-0">
                      {invitation.club.logo ? (
                        <img
                          src={invitation.club.logo}
                          alt={invitation.club.club_name}
                          className="w-16 h-16 rounded-lg object-cover border-2 border-dark-400"
                          onError={(e) => {
                            e.currentTarget.src = 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png';
                          }}
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-dark-400 flex items-center justify-center">
                          <Shield className="h-8 w-8 text-gold-400" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <h4 className="text-white font-medium">{invitation.club.club_name}</h4>
                      {invitation.club.city && (
                        <div className="text-dark-800 text-sm flex items-center mt-1">
                          <MapPin className="h-3.5 w-3.5 mr-1" />
                          {invitation.club.city}
                        </div>
                      )}
                      <div className="text-dark-800 text-sm mt-2">
                        Invited by {invitation.inviter.full_name} • {new Date(invitation.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-2 mt-3 sm:mt-0">
                      <Button
                        onClick={() => handleAcceptInvitation(invitation.id)}
                        disabled={isLoading}
                        className="bg-gold-400 hover:bg-gold-500 text-dark-50"
                        size="sm"
                      >
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            Accept
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleDeclineInvitation(invitation.id)}
                        disabled={isLoading}
                        variant="outline"
                        size="sm"
                        className="border-dark-400 text-white hover:bg-dark-400"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Decline
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* User's clubs list */}
      <div className="space-y-4 mb-6">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 text-gold-400 animate-spin" />
          </div>
        ) : userClubs.length > 0 ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {userClubs.map((club) => (
                <div
                  onClick={(e) => navigateToClub(club.club_id, e)}
                  key={club.club_id}
                  className={cn(
                    "bg-dark-300 rounded-lg p-4 flex flex-col items-center text-center",
                    "transition-all duration-200 hover:bg-dark-400 cursor-pointer",
                    "border border-transparent",
                    club.is_primary && "border-gold-400/50"
                  )}
                >
                  <div className="relative mb-3">
                    {club.logo ? (
                      <img
                        src={club.logo}
                        alt={club.club_name}
                        className={cn(
                          "w-16 h-16 rounded-full object-cover",
                          "border-2",
                          club.is_primary ? "border-gold-400" : "border-dark-400"
                        )}
                        onError={(e) => {
                          // Replace the broken image with a div containing the shield icon
                          const parent = e.currentTarget.parentNode;
                          if (parent) {
                            // Create shield container
                            const container = document.createElement('div');
                            container.className = club.is_primary 
                              ? "w-16 h-16 rounded-full flex items-center justify-center border-2 border-gold-400 bg-gold-400/10"
                              : "w-16 h-16 rounded-full flex items-center justify-center border-2 border-dark-400 bg-dark-400";
                            
                            // Create SVG shield icon
                            const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                            svg.setAttribute("viewBox", "0 0 24 24");
                            svg.setAttribute("width", "32");
                            svg.setAttribute("height", "32");
                            svg.setAttribute("fill", "none");
                            svg.setAttribute("stroke", "currentColor");
                            svg.setAttribute("stroke-width", "2");
                            svg.setAttribute("stroke-linecap", "round");
                            svg.setAttribute("stroke-linejoin", "round");
                            svg.setAttribute("class", "text-gold-400");
                            
                            // Create shield path
                            const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
                            path.setAttribute("d", "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z");
                            
                            svg.appendChild(path);
                            container.appendChild(svg);
                            
                            // Replace the img with the container
                            parent.replaceChild(container, e.currentTarget);
                          }
                        }}
                      />
                    ) : renderDefaultClubLogo(club.is_primary || false)}
                    {club.is_primary && (
                      <div className="absolute -top-1 -right-1 bg-gold-400 text-dark-50 rounded-full w-6 h-6 flex items-center justify-center">
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                  <div className="mb-2">
                    <div className="text-white font-medium text-sm line-clamp-1" title={club.club_name}>
                      {club.club_name}
                    </div>
                    {club.city && (
                      <div className="text-dark-800 text-xs line-clamp-1" title={club.city}>
                        {club.city}
                      </div>
                    )}
                  </div>
                  <div className="mt-auto pt-2 flex flex-col gap-2 w-full">
                    {!club.is_primary && (
                      <Button
                        onClick={() => setPrimaryClub(club.user_club_id!, club.club_id)}
                        variant="outline"
                        size="sm"
                        className="text-xs border-dark-400 hover:bg-dark-400 w-full"
                        disabled={isLoading}
                      >
                        Set Primary
                      </Button>
                    )}
                    <Button
                      onClick={() => removeClubMembership(club.user_club_id!, club.is_primary!)}
                      variant="outline"
                      size="sm"
                      className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 w-full"
                      disabled={isLoading}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {userClubs.map((club) => (
                <div
                  onClick={(e) => navigateToClub(club.club_id, e)}
                  key={club.club_id}
                  className="bg-dark-300 rounded-lg p-3 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between cursor-pointer hover:bg-dark-400 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {club.logo ? (
                      <img
                        src={club.logo}
                        alt={club.club_name}
                        className={cn(
                          "w-10 h-10 rounded-full object-cover",
                          "border-2",
                          club.is_primary ? "border-gold-400" : "border-dark-400"
                        )}
                        onError={(e) => {
                          // Create a div with shield icon to replace the broken image
                          const parent = e.currentTarget.parentNode;
                          if (parent) {
                            // Create shield container
                            const container = document.createElement('div');
                            container.className = club.is_primary 
                              ? "w-10 h-10 rounded-full flex items-center justify-center border-2 border-gold-400 bg-gold-400/10"
                              : "w-10 h-10 rounded-full flex items-center justify-center border-2 border-dark-400 bg-dark-400";
                            
                            // Create SVG shield icon
                            const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                            svg.setAttribute("viewBox", "0 0 24 24");
                            svg.setAttribute("width", "20");
                            svg.setAttribute("height", "20");
                            svg.setAttribute("fill", "none");
                            svg.setAttribute("stroke", "currentColor");
                            svg.setAttribute("stroke-width", "2");
                            svg.setAttribute("stroke-linecap", "round");
                            svg.setAttribute("stroke-linejoin", "round");
                            svg.setAttribute("class", "text-gold-400");
                            
                            // Create shield path
                            const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
                            path.setAttribute("d", "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z");
                            
                            svg.appendChild(path);
                            container.appendChild(svg);
                            
                            // Replace the img with the container
                            parent.replaceChild(container, e.currentTarget);
                          }
                        }}
                      />
                    ) : (
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center",
                        "border-2",
                        club.is_primary ? "border-gold-400 bg-gold-400/10" : "border-dark-400 bg-dark-400"
                      )}>
                        <Shield className="h-5 w-5 text-gold-400" />
                      </div>
                    )}
                    <div>
                      <div className="text-white font-medium flex flex-wrap items-center gap-2">
                        {club.club_name}
                        {club.is_primary && (
                          <span className="text-xs bg-gold-400/20 text-gold-400 px-2 py-0.5 rounded-full">
                            Primary
                          </span>
                        )}
                      </div>
                      {club.city && (
                        <div className="text-dark-800 text-sm">{club.city}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 self-end sm:self-auto mt-2 sm:mt-0">
                    {!club.is_primary && (
                      <Button
                        onClick={() => setPrimaryClub(club.user_club_id!, club.club_id)}
                        variant="outline"
                        size="sm"
                        className="text-xs border-dark-400 hover:bg-dark-400"
                        disabled={isLoading}
                      >
                        Set Primary
                      </Button>
                    )}
                    <Button
                      onClick={() => removeClubMembership(club.user_club_id!, club.is_primary!)}
                      variant="outline"
                      size="sm"
                      className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50"
                      disabled={isLoading}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="text-center py-6 bg-dark-300 rounded-lg">
            <Shield className="h-10 w-10 text-dark-800 mx-auto mb-2" />
            <p className="text-dark-800">No club memberships yet</p>
          </div>
        )}
      </div>

      {/* Add club button */}
      {!showSearch ? (
        <Button
          onClick={() => setShowSearch(true)}
          className="w-full bg-dark-300 hover:bg-dark-400 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Club Membership
        </Button>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-medium">Search Clubs</h3>
            <Button
              onClick={() => {
                setShowSearch(false);
                setSearchTerm('');
                setClubResults([]);
              }}
              variant="ghost"
              size="sm"
              className="text-dark-800 hover:text-white"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search clubs by name..."
              className="w-full bg-dark-300 border border-dark-400 rounded-lg px-3 py-2 text-white placeholder-dark-800"
            />
            {isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Loader2 className="h-4 w-4 text-dark-800 animate-spin" />
              </div>
            )}
          </div>

          {/* Search results */}
          {clubResults.length > 0 ? (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {clubResults.map((club) => (
                <div 
                  key={club.club_id} 
                  className="bg-dark-300 rounded-lg p-3 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between hover:bg-dark-400 transition-colors cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    addClubMembership(club.club_id);
                  }}
                >
                  <div className="flex items-center space-x-3">
                    {club.logo ? (
                      <img
                        src={club.logo}
                        alt={club.club_name}
                        className="w-8 h-8 rounded-full object-cover border-2 border-dark-400"
                        onError={(e) => {
                          // Create a div with shield icon to replace the broken image
                          const parent = e.currentTarget.parentNode;
                          if (parent) {
                            // Create shield container
                            const container = document.createElement('div');
                            container.className = "w-8 h-8 rounded-full flex items-center justify-center border-2 border-dark-400 bg-dark-400";
                            
                            // Create SVG shield icon
                            const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                            svg.setAttribute("viewBox", "0 0 24 24");
                            svg.setAttribute("width", "16");
                            svg.setAttribute("height", "16");
                            svg.setAttribute("fill", "none");
                            svg.setAttribute("stroke", "currentColor");
                            svg.setAttribute("stroke-width", "2");
                            svg.setAttribute("stroke-linecap", "round");
                            svg.setAttribute("stroke-linejoin", "round");
                            svg.setAttribute("class", "text-gold-400");
                            
                            // Create shield path
                            const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
                            path.setAttribute("d", "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z");
                            
                            svg.appendChild(path);
                            container.appendChild(svg);
                            
                            // Replace the img with the container
                            parent.replaceChild(container, e.currentTarget);
                          }
                        }}
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-dark-400 flex items-center justify-center border-2 border-dark-400">
                        <Shield className="h-4 w-4 text-gold-400" />
                      </div>
                    )}
                    <div>
                      <div className="text-white font-medium">{club.club_name}</div>
                      {club.city && (
                        <div className="text-dark-800 text-xs">{club.city}</div>
                      )}
                    </div>
                  </div>
                  <div className="self-end sm:self-auto">
                    <Button
                      size="sm"
                      className="bg-gold-400 hover:bg-gold-500 text-dark-50 text-xs"
                      disabled={isLoading}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : searchTerm.length > 1 && !isSearching ? (
            <div className="text-center py-4 bg-dark-300 rounded-lg">
              <p className="text-dark-800 mb-3">No clubs found with that name</p>
              <div className="flex flex-wrap items-center justify-center text-dark-800 text-sm px-2">
                <AlertCircle className="h-4 w-4 mr-1 text-dark-800" />
                <span>Only existing clubs can be selected</span>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
